import { createHandle } from "./hooks/handle.js";
import { createReroute } from "./hooks/reroute.js";
import { base } from "$app/paths";
import { page } from "$app/stores";
import { get } from "svelte/store";
import { browser, dev } from "$app/environment";
import { getTranslatedPath } from "./path-translations/getTranslatedPath.js";
import { serializeRoute } from "./utils/serialize-path.js";
import { getCanonicalPath } from "./path-translations/getCanonicalPath.js";
import { getPathInfo } from "./utils/get-path-info.js";
import { normaliseBase as canonicalNormaliseBase } from "./utils/normaliseBase.js";
import { resolve } from "./utils/path.js";
import { createExclude } from "./exclude.js";
import { guessTextDirMap } from "./utils/text-dir.js";
import { resolvePathTranslations } from "./config/resolvePathTranslations.js";
import { validatePathTranslations } from "./config/validatePathTranslations.js";
/**
 * Creates an i18n instance that manages your internationalization.
 *
 * @param runtime The Paraglide runtime.
 * @param options The options for the i18n instance.
 * @returns An i18n instance.
 *
 * @example
 * ```ts
 * // src/lib/i18n.js
 * import * as runtime from "../paraglide/runtime.js"
 * import { createI18n } from "@inlang/paraglide-js-adapter-sveltekit"
 *
 * export const i18n = createI18n(runtime, { ...options })
 * ```
 */
export function createI18n(runtime, options) {
    const translations = resolvePathTranslations(options?.pathnames ?? {}, runtime.availableLanguageTags);
    if (dev) {
        const issues = validatePathTranslations(translations, runtime.availableLanguageTags);
        if (issues.length) {
            console.warn(`The following issues were found in your path translations. Make sure to fix them before deploying your app:`);
            console.table(issues);
        }
    }
    const excludeConfig = options?.exclude ?? [];
    const defaultLanguageTag = options?.defaultLanguageTag ?? runtime.sourceLanguageTag;
    const config = {
        runtime,
        translations,
        exclude: createExclude(excludeConfig),
        defaultLanguageTag,
        prefixDefaultLanguage: options?.prefixDefaultLanguage ?? "never",
        textDirection: options?.textDirection ?? guessTextDirMap(runtime.availableLanguageTags),
        seo: {
            noAlternateLinks: options?.seo?.noAlternateLinks ?? false,
        },
    };
    // We don't want the translations to be mutable
    Object.freeze(translations);
    Object.freeze(config);
    return {
        /**
         * The configuration that was used to create this i18n instance.
         */
        config,
        /**
         * Returns a `reroute` hook that applies the path translations to the paths.
         * Register it in your `src/hooks.js` file to enable path translations.
         *
         * @example
         * ```ts
         * // src/hooks.js
         * import { i18n } from "../lib/i18n.js"
         * export const reroute = i18n.reroute()
         * ```
         */
        reroute: () => createReroute(config),
        /**
         * Returns a `handle` hook that set's the correct `lang` attribute
         * on the `html` element
         *
         * SERVER ONLY
         */
        handle: (options = {}) => {
            if (!browser) {
                //We only want this on the server
                return createHandle(config, options);
            }
            throw new Error(dev ? "`i18n.handle` hook should only be used on the server." : "");
        },
        /**
         * Takes in a URL and returns the language that should be used for it.
         *
         * @param url
         * @returns
         */
        getLanguageFromUrl(url) {
            const pathWithLanguage = url.pathname.slice(normaliseBase(base).length);
            const lang = pathWithLanguage.split("/").filter(Boolean).at(0);
            if (runtime.isAvailableLanguageTag(lang))
                return lang;
            return defaultLanguageTag;
        },
        /**
         * Takes in a route and returns a translated version of it.
         * This is useful for use in `goto` statements and `redirect` calls.
         *
         * The oposite of `i18n.route()`.
         *
         * @param canonicalPath The path to translate (eg _/base/about_)
         * @param lang The language to translate to - Defaults to the current language
         * @returns The translated path (eg _/base/de/ueber-uns_)
         *
         * @example
         * ```ts
         * redirect(i18n.resolveRoute("/base/about", "de"))
         * ```
         */
        resolveRoute(path, lang = undefined) {
            if (config.exclude(path))
                return path;
            const normalisedBase = normaliseBase(base);
            lang = lang ?? runtime.languageTag();
            if (!path.startsWith(normalisedBase))
                return path;
            const canonicalPath = path.slice(normalisedBase.length);
            const translatedPath = getTranslatedPath(canonicalPath, lang, translations);
            return serializeRoute({
                path: translatedPath,
                lang,
                base: normalisedBase,
                dataSuffix: undefined,
                includeLanguage: true,
                defaultLanguageTag,
                prefixDefaultLanguage: config.prefixDefaultLanguage,
            });
        },
        /**
         * Takes in a path in one language and returns it's canonical version.
         * The oposite of `i18n.resolveRoute()`.
         * This is useful for use in:
         * - Language Switchers
         * - Navigation
         *
         * @param targetedPathSource The path to translate (eg _/base/de/ueber-uns_)
         * @returns The canonical version path (eg _/base/about_)
         *
         * @example
         * ```ts
         * <a
         *   href={i18n.route($page.url.pathname)}
         *   hreflang="en"
         * >
         * ```
         */
        route(translatedPath) {
            const { path, lang } = getPathInfo(translatedPath, {
                base: normaliseBase(base),
                availableLanguageTags: config.runtime.availableLanguageTags,
                defaultLanguageTag: config.defaultLanguageTag,
            });
            const canonicalPath = getCanonicalPath(path, lang, translations);
            return resolve(normaliseBase(base), canonicalPath);
        },
    };
}
function normaliseBase(base) {
    if (base === "")
        return "";
    if (base.startsWith("/"))
        return base;
    // this should only be reachable during component initialization
    // We can detect this, because base is only ever a relative path during component initialization
    return canonicalNormaliseBase(base, new URL(get(page).url));
}
